import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AgxButton,
  AgxColumn,
  AgxConditionalTextInput,
  AgxLabel,
  AgxRow,
  AgxToast,
  InputType,
  useWindowSize,
  Breakpoints,
  Images,
  AgxExpandableContent,
  StakeholderType,
} from '@urbanx/agx-ui-components';
import {
  ContractSettingsUnconditionalDisplayAs,
  ContractSettingsUnderOfferDisplayAs,
  LicenceDetailsState,
} from '@urbanx/agx.agencies.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AgxRadioGroup, AgxTextInput } from '@urbanx/agx.ui-components2';
import { FormPrompt } from 'components/FormPrompt';
import { MenuTitles, SettingsMenu } from 'constants/menu';
import { useSelectedAgency } from 'hooks';

import { ContentLayout, PageLayout } from 'layout';
import { isEqual } from 'lodash-es';
import CustomParagraphModal from 'pages/custom-paragraph/CustomParagraphModal';
import CustomParagraphSection from 'pages/custom-paragraph/CustomParagraphSection';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GetContractSettings, UpdateContractSettings } from 'services';
import {
  ContractSettings,
  CustomEmailParagraph,
  NotificationTemplates,
} from 'types/agencySettings';
import { AgxToastState } from 'types/commonTypes';

const ContractSettingView = () => {
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const { agencyId } = useParams();
  const windowSize = useWindowSize();
  const [formLoaded, setFormLoaded] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const queryClient = useQueryClient();
  const { agency: selectedAgency } = useSelectedAgency();
  const [tertiaryActiveMenu, setTertiaryActiveMenu] = useState<MenuTitles | undefined>(
    MenuTitles.CONTRACT
  );
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const [contractSettings, setContractSettings] = useState<ContractSettings>({
    contractsEmail: '',
    settlementPlace: '',
    titleEncumbrances: '',
    unconditionalDisplayAs: undefined,
    underOfferDisplayAs: undefined,
    buyerEmailParagraph: undefined,
    vendorEmailParagraph: undefined,
  });

  const [modalOpenForStakeholder, setModalOpenForStakeholder] = useState<StakeholderType | null>(
    null
  );

  const {
    data: agencyContractSettings,
    isFetched,
    isLoading,
  } = useQuery<ContractSettings | undefined>({
    queryKey: ['getContractSettings', agencyId],
    queryFn: (queryKey) => GetContractSettings(getAuthToken, queryKey.queryKey[1] as string),
    enabled: !!agencyId,
  });

  const { mutate: updateContractSettings } = useMutation({
    mutationFn: UpdateContractSettings,
    onSuccess: () => {
      setIsFormDirty(false);
      queryClient.invalidateQueries({
        queryKey: ['getContractSettings', agencyId],
      });
      updateToastState({
        color: 'success',
        message: 'Contract setting saved successfully',
        open: true,
      });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error saving contract setting',
        open: true,
      });
    },
  });

  const saveChanges = useCallback(() => {
    updateContractSettings({
      getAuthToken,
      ...contractSettings,
      AgencyId: agencyId || '',
    });
  }, [updateContractSettings, getAuthToken, contractSettings, agencyId]);

  useEffect(() => {
    if (isFetched && agencyContractSettings) {
      setContractSettings(agencyContractSettings);
      setIsFormDirty(false);
      setFormLoaded(true);
    }
  }, [agencyContractSettings]);

  useEffect(() => {
    if (isFetched && agencyContractSettings) {
      console.log(contractSettings, agencyContractSettings);
      setIsFormDirty(!isEqual(contractSettings, agencyContractSettings));
    }
  }, [contractSettings]);

  const addCustomParagraph = (stakeholder: StakeholderType) => {
    setModalOpenForStakeholder(stakeholder);
  };

  const handleSaveCustomParagraph = useCallback(
    (customEmailParagraph: CustomEmailParagraph) => {
      const updatedSettings =
        modalOpenForStakeholder === StakeholderType.Buyer
          ? { ...contractSettings, buyerEmailParagraph: customEmailParagraph }
          : { ...contractSettings, vendorEmailParagraph: customEmailParagraph };

      setContractSettings(updatedSettings);

      setIsFormDirty(true);
      setModalOpenForStakeholder(null);
    },
    [modalOpenForStakeholder, contractSettings]
  );

  const agencyStates = useMemo(
    () => selectedAgency?.companyDetails?.licences?.map((l) => l.state) || [],
    [selectedAgency]
  );

  if (!isFetched || isLoading || !formLoaded) return null;

  const contractDefaultsComponent = (
    <AgxExpandableContent title="Contract defaults" extraClasses="container100Percent" veryLargeGap>
      <AgxRow veryLargeGap>
        <AgxTextInput
          id="contractEmail"
          label="Contract Email"
          stretch
          hideOptionalText
          defaultValue={contractSettings.contractsEmail || ''}
          onInputValueChange={({ value }: { value: string }) =>
            setContractSettings((previousState) => ({
              ...previousState,
              contractsEmail: value,
            }))
          }
          type={'email'}
        />
      </AgxRow>
      <AgxRow veryLargeGap>
        <AgxTextInput
          id="SettlementPlace"
          label="Place for Settlement"
          stretch
          hideOptionalText
          defaultValue={contractSettings.settlementPlace}
          onInputValueChange={({ value }: { value: string }) =>
            setContractSettings((previousState) => ({
              ...previousState,
              settlementPlace: value,
            }))
          }
          type={'text'}
        />
      </AgxRow>
      <AgxRow veryLargeGap>
        <AgxConditionalTextInput
          id={'EncumbrancesEasements'}
          key={`${'EncumbrancesEasements'}`}
          onValueChanged={({ value }) =>
            setContractSettings((previousState) => ({
              ...previousState,
              titleEncumbrances: value !== 'Nil' ? value : '',
            }))
          }
          defaultValue={
            contractSettings.titleEncumbrances?.trim() ? contractSettings.titleEncumbrances : 'Nil'
          }
          label={'Title Encumbrances'}
          inputType={InputType.TextArea}
          noLabel={'No Title encumbrances'}
          yesLabel={'Yes, property is subject to Title encumbrances'}
          placeholder="Encumbrance details"
          noTextInputValue="Nil"
          flexibleHeight
          reverseLabelOrder
          showTextInputOnYesSelection
        />
      </AgxRow>
    </AgxExpandableContent>
  );

  const listingStatusComponent = (
    <AgxExpandableContent
      title="REX Listing Status Preference"
      extraClasses="container100Percent"
      veryLargeGap
    >
      <AgxRow veryLargeGap>
        <AgxRadioGroup
          id="underOfferDisplayAs"
          key="underOfferDisplayAs"
          label="When Contract 'Accepted', set listing status to:"
          options={[
            {
              label: 'Available',
              value: ContractSettingsUnderOfferDisplayAs.Available,
            },
            {
              label: 'Under Offer',
              value: ContractSettingsUnderOfferDisplayAs.UnderOffer,
            },
          ]}
          value={(
            contractSettings.underOfferDisplayAs ?? ContractSettingsUnderOfferDisplayAs.Available
          ).toString()}
          onChange={(value) =>
            setContractSettings((previousState) => ({
              ...previousState,
              underOfferDisplayAs:
                value !== undefined
                  ? (value as unknown as ContractSettingsUnderOfferDisplayAs)
                  : undefined,
            }))
          }
          layout={'vertical'}
        />
      </AgxRow>
      <AgxRow veryLargeGap>
        <AgxRadioGroup
          id="unconditionalDisplayAs"
          key="unconditionalDisplayAs"
          label="When Contract 'Unconditional', set listing status to:"
          options={[
            {
              label: 'Under Offer',
              value: ContractSettingsUnconditionalDisplayAs.UnderOffer,
            },
            {
              label: 'Sold',
              value: ContractSettingsUnconditionalDisplayAs.Sold,
            },
            {
              label: 'Do not update listing status',
              value: ContractSettingsUnconditionalDisplayAs.DoNotUpdate,
            },
          ]}
          value={(
            contractSettings.unconditionalDisplayAs ?? ContractSettingsUnconditionalDisplayAs.Sold
          ).toString()}
          onChange={(value) =>
            setContractSettings((previousState) => ({
              ...previousState,
              unconditionalDisplayAs:
                value !== undefined
                  ? (value as unknown as ContractSettingsUnconditionalDisplayAs)
                  : undefined,
            }))
          }
          layout={'vertical'}
        />
      </AgxRow>
    </AgxExpandableContent>
  );

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryMenu={SettingsMenu}
        tertiaryActiveMenu={tertiaryActiveMenu}
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn veryLargeGap>
          <AgxRow
            spaceBetween
            centered
            extraClasses={`borderBottomContainer ${windowSize === Breakpoints.Desktop ? 'pageTitleContainerWithTertiaryMenu' : ''}`}
          >
            {windowSize === Breakpoints.Desktop && <AgxLabel large>{MenuTitles.SETTINGS}</AgxLabel>}
            {windowSize !== Breakpoints.Desktop && (
              <AgxButton
                id="btn-back"
                leftIcon={<Images.ArrowBackOutline />}
                naked
                large
                text={MenuTitles.CONTRACT}
                onClick={() => setTertiaryActiveMenu(undefined)}
              />
            )}

            <AgxButton
              text="Save Changes"
              medium
              primary
              onClick={saveChanges}
              disabled={!isFormDirty}
            />
          </AgxRow>
          {agencyStates?.includes(LicenceDetailsState.QLD) && contractDefaultsComponent}
          {listingStatusComponent}
          {agencyStates?.includes(LicenceDetailsState.QLD) && (
            <>
              {[StakeholderType.Buyer, StakeholderType.Vendor].map((type) => (
                <CustomParagraphSection
                  key={type}
                  title={`Executed Contract to ${type}`}
                  componentKey={`executedContractEmailTo${type}`}
                  customEmailParagraph={
                    type === StakeholderType.Buyer
                      ? contractSettings.buyerEmailParagraph
                      : contractSettings.vendorEmailParagraph
                  }
                  onAddCustomParagraph={() => addCustomParagraph(type)}
                />
              ))}
            </>
          )}
        </AgxColumn>
        {modalOpenForStakeholder ? (
          <CustomParagraphModal
            key={`modal-${modalOpenForStakeholder}`}
            customEmailParagraph={
              modalOpenForStakeholder === StakeholderType.Buyer
                ? contractSettings.buyerEmailParagraph
                : contractSettings.vendorEmailParagraph
            }
            title={`Executed Contract Email to ${modalOpenForStakeholder}`}
            initialState={LicenceDetailsState.QLD}
            templateName={
              modalOpenForStakeholder === StakeholderType.Buyer
                ? NotificationTemplates.ExecutedContractBuyerTemplate
                : NotificationTemplates.ExecutedContractVendorTemplate
            }
            saveCustomParagraph={handleSaveCustomParagraph}
            setIsDialogOpen={() => setModalOpenForStakeholder(null)}
          />
        ) : (
          <></>
        )}
      </ContentLayout>
    </PageLayout>
  );
};

export default ContractSettingView;
